import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PHONE_PRIMARY, PHONE_PRIMARY_TEL } from "@/lib/constants";
import { CATEGORIES } from "@/lib/seo-categories";
import { getSeoPage } from "@/lib/seo-content";
import logo from "@/assets/logo/logo.jpg";

const ADVANCED_TREATMENTS = [
  { name: "Invisalign", path: "/invisalign-kalyan" },
  { name: "Dental Implants", path: "/dental-implants-kalyan" },
  { name: "Root Canal", path: "/root-canal-kalyan" },
  { name: "Teeth Whitening", path: "/teeth-whitening-kalyan" },
];

// Build mega-menu items from SEO categories. Show service name from SEO content h1 fallback.
const getServiceLabel = (slug: string): string => {
  const p = getSeoPage(slug);
  if (!p) return slug;
  // Strip "in Kalyan" / "Kalyan" + dashes for compact menu labels
  return p.h1.split(" — ")[0].split(" in Kalyan")[0].replace(/Kalyan$/i, "").trim();
};

const navLinks = [
  { name: "Home", path: "/", icon: Home },
  { name: "About", path: "/about" },
  { name: "Services", path: "/services", hasDropdown: true },
  { name: "Advanced Treatments", path: "/invisalign-kalyan", hasAdvancedDropdown: true, featured: true },
  { name: "Our Doctors", path: "/associate-doctors" },
  { name: "Smile Gallery", path: "/smile-gallery" },
  { name: "Blog", path: "/blogs" },
  { name: "Contact", path: "/contact" },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState<string | null>(null);
  const location = useLocation();

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
      <div className="container flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <img
            src={logo}
            alt="Mutha's Advanced Dental Care logo"
            width="220"
            height="80"
            className="h-10 md:h-14 w-auto rounded"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-0.5">
          {navLinks.map((link) => (
            <div key={link.name} className="relative group">
              <Link
                to={link.path}
                className={`flex items-center gap-1 px-3 py-2 text-[13px] font-heading font-medium uppercase tracking-[0.05em] rounded-md transition-colors hover:text-primary ${
                  link.featured
                    ? "text-primary border border-gold/60 hover:bg-primary hover:text-primary-foreground hover:border-primary"
                    : isActive(link.path) ? "text-primary" : "text-foreground"
                }`}
              >
                {link.icon && <link.icon className="h-4 w-4" />}
                {link.name}
                {(link.hasDropdown || link.hasAdvancedDropdown) && <ChevronDown className="h-3.5 w-3.5" />}
              </Link>
              {link.hasDropdown && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-[min(1100px,95vw)] bg-card border border-border rounded-xl shadow-soft-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="grid grid-cols-5 gap-0 p-4">
                    {CATEGORIES.map((cat) => {
                      const Icon = cat.icon;
                      return (
                        <div key={cat.key} className="px-3 border-r border-border last:border-r-0">
                          <Link
                            to={`/services/${cat.hubSlug}`}
                            className="flex items-center gap-2 mb-2 text-primary hover:text-[hsl(var(--primary-deep))]"
                          >
                            <Icon className="h-4 w-4" />
                            <span className="text-eyebrow !text-primary">{cat.shortName}</span>
                          </Link>
                          <ul className="space-y-1">
                            {cat.serviceSlugs.map((slug) => (
                              <li key={slug}>
                                <Link
                                  to={`/services/${slug}`}
                                  className="block px-2 py-1.5 text-[13px] font-body rounded-md text-foreground/85 hover:bg-accent hover:text-primary transition-colors"
                                >
                                  {getServiceLabel(slug)}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                  <div className="border-t border-border bg-muted/30 px-4 py-2.5 rounded-b-xl">
                    <Link to="/services" className="text-xs font-heading uppercase tracking-wider text-primary hover:underline">
                      View all services →
                    </Link>
                  </div>
                </div>
              )}
              {link.hasAdvancedDropdown && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-card border border-border rounded-lg shadow-soft-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="p-2">
                    {ADVANCED_TREATMENTS.map((t) => (
                      <Link
                        key={t.path}
                        to={t.path}
                        className="block px-3 py-2 text-sm font-body rounded-md hover:bg-accent hover:text-primary transition-colors"
                      >
                        {t.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Desktop Right — Book */}
        <div className="hidden lg:flex items-center gap-2">
          <Button asChild variant="gold" size="sm">
            <Link to="/book-appointment">Book</Link>
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="lg:hidden h-11 w-11 shrink-0"
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
          aria-controls="mobile-navigation"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div id="mobile-navigation" className="lg:hidden border-t border-border bg-card max-h-[calc(100dvh-8rem)] overflow-y-auto overscroll-contain">
          <nav className="container py-4 space-y-1">
            {navLinks.map((link) => (
              <div key={link.name}>
                {link.hasDropdown ? (
                  <>
                    <button
                      onClick={() => setServicesOpen(!servicesOpen)}
                      className="flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium rounded-md hover:bg-accent"
                    >
                      {link.name}
                      <ChevronDown className={`h-4 w-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                    </button>
                    {servicesOpen && (
                      <div className="pl-2 space-y-1">
                        <Link
                          to="/services"
                          onClick={() => setMobileOpen(false)}
                          className="block px-3 py-2 text-sm rounded-md hover:bg-accent text-primary font-medium"
                        >
                          All Services
                        </Link>
                        {CATEGORIES.map((cat) => (
                          <div key={cat.key}>
                            <button
                              onClick={() => setMobileCatOpen(mobileCatOpen === cat.key ? null : cat.key)}
                              className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-md hover:bg-accent"
                            >
                              <span className="flex items-center gap-2">
                                <cat.icon className="h-4 w-4 text-primary" />
                                {cat.shortName}
                              </span>
                              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${mobileCatOpen === cat.key ? "rotate-180" : ""}`} />
                            </button>
                            {mobileCatOpen === cat.key && (
                              <div className="pl-6 space-y-0.5 pb-1">
                                {cat.serviceSlugs.map((slug) => (
                                  <Link
                                    key={slug}
                                    to={`/services/${slug}`}
                                    onClick={() => setMobileOpen(false)}
                                    className="block px-3 py-1.5 text-[13px] rounded-md hover:bg-accent text-foreground/80"
                                  >
                                    {getServiceLabel(slug)}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : link.hasAdvancedDropdown ? (
                  <>
                    <button
                      onClick={() => setAdvancedOpen(!advancedOpen)}
                      className="flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium rounded-md hover:bg-accent text-primary"
                    >
                      {link.name}
                      <ChevronDown className={`h-4 w-4 transition-transform ${advancedOpen ? "rotate-180" : ""}`} />
                    </button>
                    {advancedOpen && (
                      <div className="pl-4 space-y-1">
                        {ADVANCED_TREATMENTS.map((t) => (
                          <Link
                            key={t.path}
                            to={t.path}
                            onClick={() => setMobileOpen(false)}
                            className="block px-3 py-2 text-sm rounded-md hover:bg-accent"
                          >
                            {t.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={link.path}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-md hover:bg-accent ${
                      isActive(link.path) ? "text-primary" : ""
                    }`}
                  >
                    {link.icon && <link.icon className="h-4 w-4" />}
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
            {/* Phone in mobile menu */}
            <a href={PHONE_PRIMARY_TEL} className="flex items-center gap-2 px-3 py-2.5 text-sm font-semibold text-primary">
              <Phone className="h-4 w-4" /> {PHONE_PRIMARY}
            </a>
            <div className="pt-3 border-t border-border">
              <Button asChild className="w-full">
                <Link to="/book-appointment" onClick={() => setMobileOpen(false)}>Book Appointment</Link>
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
